'use client';

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { getTranslations, type Locale, type Translations } from './translations';

// ─── Context Type ────────────────────────────────────────────

interface LanguageContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translations;
  /**
   * Formatea un valor como porcentaje respetando el idioma activo.
   * - Castellano: `50,5%` (símbolo después)
   * - Euskera:    `% 50,5` (símbolo antes)
   *
   * @param value     Valor numérico a formatear.
   * @param decimals  Número de decimales (por defecto 1).
   */
  formatPercent: (value: number, decimals?: number) => string;
}

// ─── Context ─────────────────────────────────────────────────

const LanguageContext = createContext<LanguageContextType>({
  locale: 'es',
  setLocale: () => {},
  t: getTranslations('es'),
  formatPercent: (v, d = 1) => {
    const formatted = v.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d });
    return `${formatted}%`;
  },
});

// ─── Hook ────────────────────────────────────────────────────

export function useTranslation(): LanguageContextType {
  return useContext(LanguageContext);
}

// ─── Storage Key ─────────────────────────────────────────────

const STORAGE_KEY = 'ehu-mobility-locale';

function getSavedLocale(): Locale {
  if (typeof window === 'undefined') return 'es';
  
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'eu' || saved === 'es') return saved;
  } catch {
    // localStorage not available
  }
  
  // Check browser language for Basque
  try {
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('eu') || browserLang.startsWith('bas')) {
      return 'eu';
    }
  } catch {
    // navigator not available
  }
  
  return 'es';
}

// ─── Provider ────────────────────────────────────────────────

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>('es');
  const [mounted, setMounted] = useState(false);

  // Initialize from localStorage on mount
  useEffect(() => {
    const saved = getSavedLocale();
    setLocaleState(saved);
    setMounted(true);
    
    // Update HTML lang attribute
    document.documentElement.lang = saved;
  }, []);

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    
    try {
      localStorage.setItem(STORAGE_KEY, newLocale);
    } catch {
      // localStorage not available
    }
    
    // Update HTML lang attribute
    document.documentElement.lang = newLocale;
  }, []);

  const t = getTranslations(locale);

  // Formatea un porcentaje respetando el idioma activo:
  //   es → '50,5%'   |   eu → '%50,5'
  const formatPercent = useCallback((value: number, decimals: number = 1): string => {
    const localeStr = locale === 'eu' ? 'eu-ES' : 'es-ES';
    const formatted = value.toLocaleString(localeStr, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return locale === 'eu' ? `% ${formatted}` : `${formatted}%`;
  }, [locale]);

  // Prevent hydration mismatch by not rendering until mounted
  if (!mounted) {
    return (
      <LanguageContext.Provider value={{ locale: 'es', setLocale, t: getTranslations('es'), formatPercent: (v, d = 1) => {
        const formatted = v.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d });
        return `${formatted}%`;
      } }}>
        {children}
      </LanguageContext.Provider>
    );
  }

  return (
    <LanguageContext.Provider value={{ locale, setLocale, t, formatPercent }}>
      {children}
    </LanguageContext.Provider>
  );
}

export default LanguageProvider;
